import { ORG_CHAT_FORM_PLACEHOLDER } from "@/constants/chat"
import { Organization, OrganizationContext } from "@/types/organization"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"

import { env } from "@/lib/env"
import { isEuRegion } from "@/lib/region"
import type { RootState } from "./redux-store"

export type ViewSize = "xs" | "sm" | "split"

/** A staff-only pretend device fix (Home location debug). */
export type DebugPosition = {
  latitude: number
  longitude: number
  accuracy: number | null
}

export interface AppSliceState {
  debugMode: boolean
  /**
   * Home location debug, staff only and never persisted: `undefined` is the
   * real device, `null` pretends there is no fix, a position pretends to stand
   * there. Mirrors the override in current-position.ts that the request header
   * reads; it is only ever set through applyDebugPosition while debugMode is on.
   */
  debugPosition?: DebugPosition | null
  enableImpressions: boolean
  sessionChatMessageCount: number
  embedded: boolean
  organization: Organization | undefined
  organizationContext: OrganizationContext | undefined
  orgid: string | undefined
  orgUrl: string | undefined
  geoName: string | undefined
  viewSize?: ViewSize
  // The current page shows a map on its right half in split view (usePageSideMap).
  pageSideMapVisible: boolean
}

export const appSliceInitialState: AppSliceState = {
  debugMode: false,
  debugPosition: undefined,
  enableImpressions: false,
  sessionChatMessageCount: 0,
  embedded: false,
  organization: undefined,
  organizationContext: undefined,
  orgid: undefined,
  orgUrl: undefined,
  geoName: undefined,
  pageSideMapVisible: false,
}

export function canEnableDebugMode(isMindtripUser: boolean) {
  return isMindtripUser || env("NODE_ENV") === "development"
}

const appSlice = createSlice({
  name: "appSlice",
  initialState: appSliceInitialState,
  reducers: {
    setDebugMode: (state, action: PayloadAction<boolean>) => {
      state.debugMode = action.payload
    },
    setDebugPosition: (
      state,
      action: PayloadAction<DebugPosition | null | undefined>
    ) => {
      state.debugPosition = action.payload
    },
    setEnableImpressions: (state, action: PayloadAction<boolean>) => {
      state.enableImpressions = action.payload
    },
    incrementSessionChatMessageCount: (state) => {
      state.sessionChatMessageCount += 1
    },
    setEmbedded: (state, action: PayloadAction<boolean>) => {
      state.embedded = action.payload
    },
    setOrgProps: (
      state,
      action: PayloadAction<{
        organization: Organization | undefined
        organizationContext: OrganizationContext | undefined
        orgid: string | undefined
        orgUrl: string | undefined
        geoName: string | undefined
      }>
    ) => {
      state.organization = action.payload.organization
      state.organizationContext = action.payload.organizationContext
      state.orgid = action.payload.orgid
      state.orgUrl = action.payload.orgUrl
      state.geoName = action.payload.geoName
    },
    setViewSize: (state, action: PayloadAction<ViewSize>) => {
      state.viewSize = action.payload
    },
    setPageSideMapVisible: (state, action: PayloadAction<boolean>) => {
      state.pageSideMapVisible = action.payload
    },
  },
  selectors: {
    selectDebugMode: (state) => state.debugMode,
    selectDebugPosition: (state) => state.debugPosition,
    selectEnableImpressions: (state) => state.enableImpressions,
    selectSessionChatMessageCount: (state) => state.sessionChatMessageCount,
    selectEmbedded: (state) => state.embedded,
    selectPageSideMapVisible: (state) => state.pageSideMapVisible,
  },
})

export const {
  setDebugMode,
  setDebugPosition,
  setEnableImpressions,
  incrementSessionChatMessageCount,
  setEmbedded,
  setOrgProps,
  setViewSize,
  setPageSideMapVisible,
} = appSlice.actions

export const {
  selectDebugMode,
  selectDebugPosition,
  selectEnableImpressions,
  selectSessionChatMessageCount,
  selectEmbedded,
  selectPageSideMapVisible,
} = appSlice.selectors

const selectSliceState = (state: RootState) => state.appSlice

export const selectViewSize = createSelector(
  [selectSliceState],
  (state) => state.viewSize
)

export const selectIsXS = createSelector(
  [selectViewSize],
  (viewSize) => viewSize === "xs"
)

export const selectIsSM = createSelector(
  [selectViewSize],
  (viewSize) => viewSize === "sm"
)

export const selectIsSplitView = createSelector(
  [selectViewSize],
  (viewSize) => viewSize === "split"
)

// Long-term memory UI is only available in the US first-party app.
export const selectMemoryFeatureAvailable = createSelector(
  [selectEmbedded],
  (embedded) => !embedded && !isEuRegion()
)

export const selectOrganization = createSelector(
  [selectSliceState],
  (state) => state.organization
)

// embedded explore opens on the org's starting location, not the user's
export const selectOrgExploreLocationRef = createSelector(
  [selectEmbedded, selectOrganization],
  (embedded, organization) =>
    embedded ? organization?.locations?.[0]?.entity_ref : undefined
)

export const selectOrganizationExamples = createSelector(
  [selectOrganization],
  (organization) => organization?.properties.example_user_messages
)

export const selectChatPlaceholder = createSelector(
  [selectOrganization],
  (organization) =>
    organization
      ? organization.properties.chat_placeholder || ORG_CHAT_FORM_PLACEHOLDER
      : undefined
)

export const selectDisclaimerText = createSelector(
  [selectOrganization],
  (organization) =>
    organization?.properties.disclaimer_line ||
    "Mindtrip can make mistakes. Check important info."
)

export const selectBookDirectOnly = createSelector(
  [selectOrganization],
  // Locked to true in EU — org admins cannot enable OTA/embedded booking in the EU region
  (organization) => isEuRegion() || !!organization?.properties.book_direct_only
)

export const selectFlightCheckoutEnabled = createSelector(
  [selectOrganization],
  (organization) => {
    if (isEuRegion()) return false // Locked to false in EU
    if (!organization) return true
    return !!organization.properties.flight_checkout_enabled
  }
)

export const selectPartnerBookingUrlPrefix = createSelector(
  [selectOrganization],
  (organization) => organization?.properties.partner_booking_url
)

// embedded links for Myrtle Beach are handled by embed.js in parent window
export const selectIgnoreDirectBookLinks = createSelector(
  [selectOrganization],
  (organization) => !!organization?.properties.ignore_external_web_links
)

export const selectOrgHasNewsletter = createSelector(
  [selectOrganization],
  (organization) =>
    organization?.properties.show_newsletter_subscription !== false
)

export const selectOrgNewsletterDefaultChecked = createSelector(
  [selectOrganization],
  (organization) =>
    organization?.properties.newsletter_subscription_default_checked !== false
)

export const selectShowReviewHighlights = createSelector(
  [selectOrganization],
  (organization) => !!organization?.properties.show_review_highlights
)

export const selectOrganizationBusinessType = createSelector(
  [selectOrganization],
  (organization) => organization?.business_type
)

// the org's configured home country code, for currency and map display
export const selectOrgDefaultRegion = createSelector(
  [selectOrganization],
  (organization) => organization?.properties.default_region || undefined
)

// how the org frames the map before a chat has places
export const selectOrgDefaultMapView = createSelector(
  [selectOrganization],
  (organization) => {
    const viewport = organization?.properties.map_viewport
    if (!viewport) return undefined
    return {
      center: { lat: viewport.lat, lng: viewport.lng },
      zoom: viewport.zoom,
    }
  }
)

export const selectOrganizationContext = createSelector(
  [selectSliceState],
  (state) => state.organizationContext
)

export const selectEmbeddedGeoHint = createSelector(
  [selectOrganizationContext],
  (organizationContext) =>
    organizationContext?.geo_name || organizationContext?.name
)

export default appSlice.reducer
